'use client'

import { useState, useCallback, useMemo, useEffect } from 'react'
import { useBirhaus } from '@birhaus/provider'
import { convertCurrency } from '../utils/currency'
import type { 
  FinancialMetric, 
  MetricType, 
  MetricTrend,
  UseFinancialMetricsReturn,
  CurrencyCode,
  Transaction,
  Account,
  Budget
} from '../types/financial'

/**
 * useFinancialMetrics - Hook for financial KPI calculation and management
 * 
 * Features:
 * - Real-time financial metric calculation from transactions and accounts
 * - Trend analysis with historical comparisons
 * - Goal tracking and progress monitoring
 * - Multi-currency support with automatic conversion
 * - Spanish-first metric names and descriptions
 * - Performance optimization with memoized calculations
 * - Alert system for concerning financial trends
 * - Export functionality for financial reports
 * - Customizable metric periods and groupings
 */
export function useFinancialMetrics(
  accounts?: Account[],
  transactions?: Transaction[],
  budgets?: Budget[]
): UseFinancialMetricsReturn {
  const { language, reportViolation } = useBirhaus()
  
  // Metrics state
  const [metrics, setMetrics] = useState<FinancialMetric[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [baseCurrency, setBaseCurrency] = useState<CurrencyCode>('PYG')
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('monthly')

  // Calculate financial metrics
  const calculateMetrics = useCallback(async () => {
    if (!accounts || !transactions) return

    setLoading(true)
    setError(null)

    try {
      const now = new Date()
      const currentPeriodStart = new Date()
      const previousPeriodStart = new Date()
      const previousPeriodEnd = new Date()

      // Set period boundaries
      switch (period) {
        case 'daily':
          currentPeriodStart.setHours(0, 0, 0, 0)
          previousPeriodEnd.setDate(now.getDate() - 1)
          previousPeriodEnd.setHours(23, 59, 59, 999)
          previousPeriodStart.setDate(now.getDate() - 1)
          previousPeriodStart.setHours(0, 0, 0, 0)
          break
        case 'weekly':
          currentPeriodStart.setDate(now.getDate() - now.getDay())
          previousPeriodEnd.setDate(currentPeriodStart.getDate() - 1)
          previousPeriodStart.setDate(currentPeriodStart.getDate() - 7)
          break
        case 'monthly':
          currentPeriodStart.setDate(1)
          previousPeriodEnd.setMonth(now.getMonth(), 0)
          previousPeriodStart.setMonth(now.getMonth() - 1, 1)
          break
        case 'yearly':
          currentPeriodStart.setMonth(0, 1)
          previousPeriodEnd.setFullYear(now.getFullYear() - 1, 11, 31)
          previousPeriodStart.setFullYear(now.getFullYear() - 1, 0, 1)
          break
      }

      // Filter transactions for current and previous periods
      const currentTransactions = transactions.filter(t => 
        new Date(t.fecha) >= currentPeriodStart && new Date(t.fecha) <= now
      )
      
      const previousTransactions = transactions.filter(t => 
        new Date(t.fecha) >= previousPeriodStart && new Date(t.fecha) <= previousPeriodEnd
      )

      const calculatedMetrics: FinancialMetric[] = []

      // 1. Total Balance
      const totalBalance = accounts.reduce((sum, account) => {
        if (account.moneda === baseCurrency) {
          return sum + account.saldo
        }
        return sum + convertCurrency(account.saldo, account.moneda, baseCurrency)
      }, 0)

      calculatedMetrics.push({
        id: 'balance',
        tipo: 'saldo',
        nombreEs: 'Saldo Total',
        nombreEn: 'Total Balance',
        valor: totalBalance,
        moneda: baseCurrency,
        descripcionEs: 'Saldo total de todas las cuentas',
        descripcionEn: 'Total balance across all accounts',
        ultimaActualizacion: now
      })

      // 2. Income (Current Period)
      const currentIncome = currentTransactions
        .filter(t => ['transferencia_entrante', 'deposito'].includes(t.tipo) && t.estado === 'completada')
        .reduce((sum, t) => {
          if (t.moneda === baseCurrency) {
            return sum + t.monto
          }
          return sum + convertCurrency(t.monto, t.moneda, baseCurrency)
        }, 0)

      const previousIncome = previousTransactions
        .filter(t => ['transferencia_entrante', 'deposito'].includes(t.tipo) && t.estado === 'completada')
        .reduce((sum, t) => {
          if (t.moneda === baseCurrency) {
            return sum + t.monto
          }
          return sum + convertCurrency(t.monto, t.moneda, baseCurrency)
        }, 0)

      const incomeTrend: MetricTrend | undefined = previousIncome > 0 
        ? {
            direccion: currentIncome > previousIncome ? 'subida' : currentIncome < previousIncome ? 'bajada' : 'estable',
            porcentaje: ((currentIncome - previousIncome) / previousIncome) * 100,
            periodo: getPeriodName(period)
          }
        : undefined

      calculatedMetrics.push({
        id: 'income',
        tipo: 'ingresos',
        nombreEs: 'Ingresos',
        nombreEn: 'Income',
        valor: currentIncome,
        moneda: baseCurrency,
        descripcionEs: `Ingresos del período ${getPeriodName(period).toLowerCase()}`,
        descripcionEn: `${getPeriodName(period)} income`,
        tendencia: incomeTrend,
        ultimaActualizacion: now
      })

      // 3. Expenses (Current Period)
      const currentExpenses = currentTransactions
        .filter(t => ['transferencia_saliente', 'retiro', 'pago_servicios', 'compra'].includes(t.tipo) && t.estado === 'completada')
        .reduce((sum, t) => {
          if (t.moneda === baseCurrency) {
            return sum + t.monto
          }
          return sum + convertCurrency(t.monto, t.moneda, baseCurrency)
        }, 0)

      const previousExpenses = previousTransactions
        .filter(t => ['transferencia_saliente', 'retiro', 'pago_servicios', 'compra'].includes(t.tipo) && t.estado === 'completada')
        .reduce((sum, t) => {
          if (t.moneda === baseCurrency) {
            return sum + t.monto
          }
          return sum + convertCurrency(t.monto, t.moneda, baseCurrency)
        }, 0)

      const expensesTrend: MetricTrend | undefined = previousExpenses > 0
        ? {
            direccion: currentExpenses > previousExpenses ? 'subida' : currentExpenses < previousExpenses ? 'bajada' : 'estable',
            porcentaje: ((currentExpenses - previousExpenses) / previousExpenses) * 100,
            periodo: getPeriodName(period)
          }
        : undefined

      calculatedMetrics.push({
        id: 'expenses',
        tipo: 'gastos',
        valor: currentExpenses,
        moneda: baseCurrency,
        nombreEs: 'Gastos',
        nombreEn: 'Expenses',
        descripcionEs: `Gastos del período ${getPeriodName(period).toLowerCase()}`,
        descripcionEn: `${getPeriodName(period)} expenses`,
        tendencia: expensesTrend,
        ultimaActualizacion: now
      })

      // 4. Savings (Income - Expenses)
      const savings = currentIncome - currentExpenses
      const previousSavings = previousIncome - previousExpenses
      
      const savingsTrend: MetricTrend | undefined = previousSavings !== 0
        ? {
            direccion: savings > previousSavings ? 'subida' : savings < previousSavings ? 'bajada' : 'estable',
            porcentaje: previousSavings > 0 ? ((savings - previousSavings) / Math.abs(previousSavings)) * 100 : 0,
            periodo: getPeriodName(period)
          }
        : undefined

      calculatedMetrics.push({
        id: 'savings',
        tipo: 'ahorros',
        valor: savings,
        moneda: baseCurrency,
        nombreEs: 'Ahorros',
        nombreEn: 'Savings',
        descripcionEs: `Ahorros del período ${getPeriodName(period).toLowerCase()}`,
        descripcionEn: `${getPeriodName(period)} savings`,
        tendencia: savingsTrend,
        ultimaActualizacion: now
      })

      // 5. Budget Adherence (if budgets provided)
      if (budgets && budgets.length > 0) {
        const totalBudget = budgets.reduce((sum, budget) => {
          const limit = budget.limite ?? budget.montoPresupuesto
          if (budget.moneda === baseCurrency) {
            return sum + limit
          }
          return sum + convertCurrency(limit, budget.moneda, baseCurrency)
        }, 0)

        const budgetUsed = budgets.reduce((sum, budget) => {
          const spent = budget.gastado ?? budget.montoGastado
          if (budget.moneda === baseCurrency) {
            return sum + spent
          }
          return sum + convertCurrency(spent, budget.moneda, baseCurrency)
        }, 0)

        const budgetAdherence = totalBudget > 0 ? (budgetUsed / totalBudget) * 100 : 0

        calculatedMetrics.push({
          id: 'budget-adherence',
          tipo: 'objetivo',
          valor: budgetAdherence,
          moneda: 'PERCENT',
          nombreEs: 'Adherencia al Presupuesto',
          nombreEn: 'Budget Adherence',
          descripcionEs: 'Adherencia al presupuesto',
          descripcionEn: 'Budget adherence',
          objetivo: {
            valor: 100,
            fechaLimite: new Date(now.getFullYear(), now.getMonth() + 1, 0)
          },
          ultimaActualizacion: now,
          informacionAdicional: [
            language === 'es' 
              ? `Presupuesto total: ${totalBudget.toLocaleString()} ${baseCurrency}`
              : `Total budget: ${totalBudget.toLocaleString()} ${baseCurrency}`,
            language === 'es'
              ? `Utilizado: ${budgetUsed.toLocaleString()} ${baseCurrency}`
              : `Used: ${budgetUsed.toLocaleString()} ${baseCurrency}`
          ]
        })
      }

      // 6. Available Credit (from credit accounts)
      const creditAccounts = accounts.filter(a => a.tipo === 'credito')
      const availableCredit = creditAccounts.reduce((sum, account) => {
        const creditLimit = account.limiteDiario || 0 // Assuming credit limit is stored in limiteDiario
        const usedCredit = Math.abs(account.saldo) // Negative balance indicates used credit
        const available = creditLimit - usedCredit
        
        if (account.moneda === baseCurrency) {
          return sum + available
        }
        return sum + convertCurrency(available, account.moneda, baseCurrency)
      }, 0)

      if (creditAccounts.length > 0) {
        calculatedMetrics.push({
          id: 'available-credit',
          tipo: 'credito_disponible',
          valor: availableCredit,
          moneda: baseCurrency,
          nombreEs: 'Crédito Disponible',
          nombreEn: 'Available Credit',
          descripcionEs: 'Crédito disponible total',
          descripcionEn: 'Total available credit',
          ultimaActualizacion: now
        })
      }

      setMetrics(calculatedMetrics)

      // BIRHAUS compliance: Alert on concerning metrics
      if (savings < 0 && Math.abs(savings) > currentIncome * 0.1) {
        reportViolation({
          type: 'performance',
          severity: 'warning',
          element: document.createElement('div'),
          message: `Negative savings rate: ${savings} ${baseCurrency}`,
          messageEs: `Tasa de ahorro negativa: ${savings} ${baseCurrency}`,
          messageEn: `Negative savings rate: ${savings} ${baseCurrency}`,
          recommendation: 'Consider budget adjustments to improve savings rate',
          birhausPrinciple: 6 // Progressive disclosure of financial health
        })
      }

    } catch (err) {
      const errorMessage = language === 'es' 
        ? 'Error al calcular métricas financieras'
        : 'Error calculating financial metrics'
      setError(new Error(errorMessage))
      console.error('Error calculating metrics:', err)
    } finally {
      setLoading(false)
    }
  }, [accounts, transactions, budgets, baseCurrency, period, language, reportViolation])

  // Get period name helper
  const getPeriodName = useCallback((periodType: string) => {
    const periods = {
      daily: { es: 'Diario', en: 'Daily' },
      weekly: { es: 'Semanal', en: 'Weekly' },
      monthly: { es: 'Mensual', en: 'Monthly' },
      yearly: { es: 'Anual', en: 'Annual' }
    }
    return language === 'es' ? periods[periodType as keyof typeof periods].es : periods[periodType as keyof typeof periods].en
  }, [language])

  // Recalculate metrics when dependencies change
  useEffect(() => {
    if (accounts && transactions) {
      calculateMetrics()
    }
  }, [calculateMetrics])

  // Get metric by type
  const getMetricByType = useCallback((type: MetricType): FinancialMetric | undefined => {
    return metrics.find(m => m.tipo === type)
  }, [metrics])

  // Get metric by ID
  const getMetricById = useCallback((id: string): FinancialMetric | undefined => {
    return metrics.find(m => m.id === id)
  }, [metrics])

  // Calculate metric change from previous period
  const getMetricChange = useCallback((metricId: string): number | null => {
    const metric = metrics.find(m => m.id === metricId)
    if (!metric?.tendencia) return null
    return metric.tendencia.porcentaje
  }, [metrics])

  // Export metrics to CSV
  const exportMetrics = useCallback(() => {
    const headers = language === 'es' 
      ? ['ID', 'Tipo', 'Valor', 'Moneda', 'Descripción', 'Tendencia %', 'Última Actualización']
      : ['ID', 'Type', 'Value', 'Currency', 'Description', 'Trend %', 'Last Updated']

    const rows = metrics.map(metric => [
      metric.id,
      metric.tipo,
      metric.valor.toString(),
      metric.moneda,
      metric.descripcion,
      metric.tendencia?.porcentaje.toFixed(2) || '0',
      metric.ultimaActualizacion?.toISOString().split('T')[0] || ''
    ])

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `metricas_financieras_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [metrics, language])

  // Get financial health score (0-100)
  const financialHealthScore = useMemo(() => {
    let score = 50 // Base score

    const balanceMetric = getMetricByType('saldo')
    const savingsMetric = getMetricByType('ahorros')
    const incomeMetric = getMetricByType('ingresos')
    const expensesMetric = getMetricByType('gastos')

    // Positive balance contributes to score
    if (balanceMetric && balanceMetric.valor > 0) {
      score += 20
    }

    // Positive savings contributes to score
    if (savingsMetric && savingsMetric.valor > 0) {
      score += 20
    }

    // Income growth contributes to score
    if (incomeMetric?.tendencia?.direccion === 'subida') {
      score += 10
    }

    // Expense reduction contributes to score
    if (expensesMetric?.tendencia?.direccion === 'bajada') {
      score += 10
    }

    // Savings rate
    if (incomeMetric && savingsMetric && incomeMetric.valor > 0) {
      const savingsRate = (savingsMetric.valor / incomeMetric.valor) * 100
      if (savingsRate >= 20) score += 15
      else if (savingsRate >= 10) score += 10
      else if (savingsRate >= 5) score += 5
      else if (savingsRate < 0) score -= 15
    }

    return Math.max(0, Math.min(100, score))
  }, [metrics, getMetricByType])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Refresh metrics
  const refresh = useCallback(() => {
    calculateMetrics()
  }, [calculateMetrics])

  return {
    metrics,
    loading,
    error,
    refreshMetrics: calculateMetrics,
    getMetricsByCategory: () => [],
    getMetricTrend: () => []
  }
}

export type { UseFinancialMetricsReturn }